import * as React from 'react';

import { ConfirmationHeading } from 'components/business/receipts/ConfirmationHeading';
import { Interpolation, Theme } from '@emotion/react';
import { Typography } from 'omakase';
import { capitalize, first, values } from 'lodash';
import { core } from '@tock/coretypes';
import { offeringPartyCollectiveItem } from '@tock/shared/utils/offerings';
import connect, { StoreType } from '@tock/shared/utils/connect';

interface ReceiptTitleProps {
  ticketType?: core.ITicketType;
  businessName?: string;
  isVisitEdit?: boolean;
  amountDue?: number;
  ownerPatron?: core.IPatron;
  confirmationId: string;
  containerCss?: Interpolation<Theme>;
  purchase: core.IPastPurchase;
}

export const ReceiptTitle: React.FunctionComponent<ReceiptTitleProps> = ({
  purchase,
  ticketType,
  businessName,
  isVisitEdit,
  amountDue,
  confirmationId,
  containerCss,
}) => {
  let title;

  const orderOrReservation = offeringPartyCollectiveItem(purchase.ticketType?.variety);
  if (isVisitEdit && amountDue && amountDue <= 0) {
    title = 'Payment complete';
  } else if (purchase.inTransfer) {
    title = 'Reservation pending transfer';
  } else if (purchase.cancelledOrRefunded) {
    title = `${capitalize(orderOrReservation)} details`;
  } else {
    title = `${capitalize(orderOrReservation)} confirmed`;
  }

  return (
    <div className={''} data-testid="reservation-title" css={containerCss}>
      {
        <>
          <ConfirmationHeading
            component="h1"
            data-testid="reservation-title"
            variant="heading02"
            showCheckmark={!purchase.cancelledOrRefunded || !!purchase.inTransfer}
          >
            {title}
          </ConfirmationHeading>
          <Typography variant="caption" color="fgSubtle" data-testid="receipt-confirmation-id">
            Confirmation: {confirmationId}
          </Typography>
        </>
      }
      {isVisitEdit && !!amountDue && amountDue <= 0 && (
        <div className="ReservationDetails-subtitleText">
          <Typography color="inherit" component="span" variant="body02">
            We hope you enjoyed your experience at {businessName}.
          </Typography>
        </div>
      )}
      {isVisitEdit && (!amountDue || amountDue > 0) && (
        <div className="ReservationDetails-subtitleText">
          <Typography color="inherit" component="span" variant="body02">
            Your order was submitted and has been sent to the kitchen.
          </Typography>
        </div>
      )}
    </div>
  );
};

/**
 * This connected version is only used in StandardHeaderRoute.tsx on mobile
 */

export default connect<ReceiptTitleProps>(ReceiptTitle, (state: StoreType) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const purchase: core.PastPurchase = first(values(state.pastPurchase.pastPurchase))!;
  return {
    ticketType: purchase?.ticketType || undefined,
    cancelledOrRefunded: purchase?.cancelledOrRefunded,
    businessName: purchase?.business?.name || undefined,
    ownerPatron: purchase?.ownerPatron || undefined,
    isVisitEdit:
      purchase?.originatedAt === 'VISIT_EDIT' || purchase?.originatedAt === 'VISIT_EDIT_DASHBOARD',
    confirmationId: purchase?.confirmationId,
  };
});
